import {
  getAllergenSubstanceById,
  getAllergenSubstancesByCategory,
} from '../label/allergen-substances';
import type { AllergenSurfacePolicy, CompositionNode, DeclaredAllergen } from './types';

/**
 * Snapshot minimo di un `Ingredient` necessario per giudicare se il suo
 * profilo allergeni e' stato dichiarato da un umano: sottoinsieme comune a
 * ogni forma in cui i chiamanti caricano l'ingrediente (riga di ricetta
 * risolta dalla BFS, soggetto di un cartello, soggetto diretto di una voce di
 * menu, nodo collegato).
 */
export interface IngredientAllergenDeclarationSnapshot {
  isCompound: boolean | null;
  allergens: readonly unknown[];
}

/**
 * Un ingrediente "mai pronunciato" (`isCompound` null e zero allergeni) non ha
 * nessuna dichiarazione: stampare o pubblicare "allergeni: nessuno" sarebbe un
 * falso (art. 44 Reg. UE 1169/2011, art. 19 D.Lgs 231/2017).
 * `Ingredient.isCompound` e' il segnale tri-stato: `null` significa che nessuno
 * ha mai salvato il form di curatela, che invia `isCompound` e `allergens` nello
 * stesso salvataggio. Un valore non nullo prova che un umano ha rivisto anche la
 * lista allergeni, pure quando l'ha lasciata vuota perche' vuota e' la verita'
 * (il sale non ha allergeni). Unico predicato per cartellino, payload firmato,
 * menu QR, aggregazione e anagrafica.
 */
export function isNeverDeclared(ingredient: IngredientAllergenDeclarationSnapshot): boolean {
  return ingredient.isCompound === null && ingredient.allergens.length === 0;
}

export function hasAnyIngredientDeclaration(ingredient: IngredientAllergenDeclarationSnapshot) {
  return !isNeverDeclared(ingredient);
}

/**
 * Lo stato allergeni di una riga e' ignoto se la riga non e' collegata
 * all'anagrafica (`null`) oppure se l'ingrediente collegato non e' mai stato
 * dichiarato. `isCompound` assente (`undefined`) vale "dichiarato": solo i
 * loader reali lo valorizzano.
 */
export function isAllergenStateUnknown(
  ingredient: { isCompound?: boolean | null; allergens: readonly unknown[] } | null,
): boolean {
  if (ingredient === null) {
    return true;
  }
  return isNeverDeclared({
    isCompound: ingredient.isCompound === undefined ? false : ingredient.isCompound,
    allergens: ingredient.allergens,
  });
}

export type CompositionGap = 'NONE' | 'MISSING_BREAKDOWN' | 'UNDECLARED_COMPOSITION';

/**
 * Se un ingrediente richiede una scomposizione (Allegato VII Parte E) che non
 * ha. Un additivo acquistato come tale (`additiveFunction`) si dichiara con
 * funzione e nome/E-number e non ne ha mai bisogno; una scomposizione o
 * un'esenzione presenti chiudono il difetto. Altrimenti `isCompound` e' il
 * segnale onesto: `false` e' una dichiarazione esplicita di ingrediente
 * semplice ("farina"); `true` e' un composto dichiarato senza elenco
 * (`MISSING_BREAKDOWN`, blocca: DEV-366); `null` non si puo' provare composto
 * (`UNDECLARED_COMPOSITION`, solo avviso per il cartellino).
 */
export function compositionGap(
  ingredient: Pick<CompositionNode, 'isCompound' | 'additiveFunction' | 'breakdownExemption'> & {
    componentCount: number;
  },
): CompositionGap {
  const hasBreakdown = ingredient.componentCount > 0 || ingredient.breakdownExemption !== null;
  if (ingredient.additiveFunction !== null || hasBreakdown) {
    return 'NONE';
  }
  if (ingredient.isCompound === true) {
    return 'MISSING_BREAKDOWN';
  }
  return ingredient.isCompound === false ? 'NONE' : 'UNDECLARED_COMPOSITION';
}

/**
 * Un «puo' contenere» gia' coperto da un «contiene» alla granularita' con cui
 * si stampa e' una contraddizione sullo stesso documento: non si ripete.
 * Con `SUBSTANCE_WHEN_KNOWN` la sostanza nota e' coperta solo dalla stessa
 * sostanza dichiarata CONTAINS. Una traccia di sola categoria («tracce di
 * frutta a guscio») e' coperta solo da un CONTAINS di categoria (senza
 * sostanza) dello stesso allergene o da un CONTAINS la cui categoria ha una
 * sola sostanza nel catalogo («latte»): «contiene: mandorle» NON dice che non
 * ci sono tracce di altra frutta a guscio, quindi si stampa anche «puo'
 * contenere: frutta a guscio» (art. 36 Reg. UE 1169/2011: dicitura
 * precauzionale, art. 21 per la sostanza). Con `CATEGORY` si stampa la
 * categoria da entrambe le parti e basta lo stesso allergene.
 */
export function isMayContainCovered(
  mayContain: Pick<DeclaredAllergen, 'allergenId' | 'substanceId' | 'substanceCatalogKey'>,
  contains: readonly Pick<DeclaredAllergen, 'allergenId' | 'substanceId' | 'substanceCatalogKey'>[],
  granularity: AllergenSurfacePolicy['mayContainGranularity'],
): boolean {
  if (granularity === 'CATEGORY') {
    return contains.some((declared) => declared.allergenId === mayContain.allergenId);
  }
  const hasKnownSubstance =
    mayContain.substanceId !== null || mayContain.substanceCatalogKey !== null;
  if (hasKnownSubstance) {
    return contains.some((declared) => isSameSubstance(declared, mayContain));
  }
  return contains.some(
    (declared) => declared.allergenId === mayContain.allergenId && coversWholeCategory(declared),
  );
}

/** Un CONTAINS di categoria, o di una sostanza che da sola esaurisce la categoria, copre ogni traccia della categoria. */
function coversWholeCategory(
  declared: Pick<DeclaredAllergen, 'substanceId' | 'substanceCatalogKey'>,
): boolean {
  if (declared.substanceId === null && declared.substanceCatalogKey === null) {
    return true;
  }
  const substance = declared.substanceCatalogKey
    ? getAllergenSubstanceById(declared.substanceCatalogKey)
    : undefined;
  return (
    substance !== undefined && getAllergenSubstancesByCategory(substance.categoryCode).length === 1
  );
}

function isSameSubstance(
  left: Pick<DeclaredAllergen, 'allergenId' | 'substanceId' | 'substanceCatalogKey'>,
  right: Pick<DeclaredAllergen, 'allergenId' | 'substanceId' | 'substanceCatalogKey'>,
): boolean {
  if (left.allergenId !== right.allergenId) {
    return false;
  }
  if (left.substanceId !== null && right.substanceId !== null) {
    return left.substanceId === right.substanceId;
  }
  return (
    left.substanceCatalogKey !== null && left.substanceCatalogKey === right.substanceCatalogKey
  );
}
